import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Upload, ArrowLeft, Lock, Trash2 } from "lucide-react";
import { DEFAULT_CONTENT, deepMerge } from "@/content/defaults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0A0A0C] border border-[rgba(212,175,55,0.22)] text-[#F7F7F8] text-sm px-3.5 py-2.5 focus:outline-none focus:border-gold transition-colors duration-300";

function Field({ label, value, onChange, textarea, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block font-mono-lux text-[10px] uppercase tracking-[0.2em] text-gold mb-1.5">{label}</span>
      {textarea ? (
        <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
      ) : (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </label>
  );
}

function ImageField({ label, value, onChange, token }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isVideo = /\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(value || "");
  const isUploaded = (value || "").startsWith("/api/files/");
  const testSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const check = await fetch(`${data.url}?v=${Date.now()}`, { cache: "no-store" }).catch(() => null);
      if (!check || !check.ok) {
        toast.error("Upload could not be verified — please try again.");
        return;
      }
      onChange(data.url);
      toast.success(data.kind === "video" ? "Clip uploaded." : "Image uploaded.");
    } catch {
      toast.error("Upload failed. Images up to 10MB, clips up to 60MB.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!isUploaded || deleting) return;
    if (!window.confirm("Permanently delete this uploaded file? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}${value}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange("");
      toast.success("File deleted. Click Publish Changes to make it live.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="md:col-span-2">
      <span className="block font-mono-lux text-[10px] uppercase tracking-[0.2em] text-gold mb-1.5">{label}</span>
      <div className="flex items-start gap-4">
        {value ? (
          isVideo ? (
            <video src={value} autoPlay muted loop playsInline className="w-20 h-20 object-cover border border-[rgba(212,175,55,0.3)] shrink-0" />
          ) : (
            <img src={value} alt={label} className="w-20 h-20 object-cover border border-[rgba(212,175,55,0.3)] shrink-0" />
          )
        ) : null}
        <div className="flex-1 flex gap-2">
          <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="Image or clip URL — or upload →" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || deleting}
            className="shrink-0 inline-flex items-center gap-2 border border-[rgba(212,175,55,0.4)] text-gold-champagne text-xs px-4 hover:bg-[rgba(212,175,55,0.08)] transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload
          </button>
          {isUploaded ? (
            <button
              type="button"
              onClick={remove}
              disabled={deleting || uploading}
              data-testid={`delete-${testSlug}`}
              className="shrink-0 inline-flex items-center gap-2 border border-[rgba(220,80,80,0.45)] text-red-300 text-xs px-4 hover:bg-[rgba(220,80,80,0.1)] transition-colors disabled:opacity-50"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
          ) : null}
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={pick} />
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section className="border border-[rgba(212,175,55,0.2)] bg-surface p-6 sm:p-8">
      <h2 className="font-serif-lux text-2xl text-[#F7F7F8] tracking-tight mb-6">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </section>
  );
}

function SubCard({ title, children }) {
  return (
    <div className="md:col-span-2 border border-[rgba(212,175,55,0.14)] p-5">
      {title ? <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold-champagne mb-4">{title}</p> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("sam_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/content`)
      .then(({ data }) => setContent(deepMerge(DEFAULT_CONTENT, data?.data || {})))
      .catch(() => setContent(deepMerge(DEFAULT_CONTENT, {})));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("sam_admin_token", data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back, Sam.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    }
  };

  const logout = () => {
    localStorage.removeItem("sam_admin_token");
    setToken("");
    setContent(null);
  };

  const set = (path) => (value) =>
    setContent((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { data: content }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Published. Changes are live for all visitors.");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("sam_admin_token");
        setToken("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-surface border border-[rgba(212,175,55,0.25)] p-8" data-testid="admin-login-form">
          <span className="w-11 h-11 border border-[rgba(212,175,55,0.5)] flex items-center justify-center mx-auto">
            <Lock size={18} className="text-gold" />
          </span>
          <h1 className="mt-5 font-serif-lux text-2xl text-center text-[#F7F7F8]">Content Studio</h1>
          <p className="mt-1.5 text-center font-mono-lux text-[10px] tracking-[0.3em] text-gold">SAM ORTEGA · ADMIN</p>
          <div className="mt-7 space-y-4">
            <Field label="Email" value={email} onChange={setEmail} />
            <label className="block">
              <span className="block font-mono-lux text-[10px] uppercase tracking-[0.2em] text-gold mb-1.5">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                data-testid="admin-login-password"
              />
            </label>
          </div>
          {error ? <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{error}</p> : null}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="btn-shimmer mt-6 w-full bg-gold text-[#0A0A0B] font-semibold text-sm tracking-wide py-3 hover:bg-gold-champagne transition-colors"
          >
            Enter Studio
          </button>
          <a href="/" className="mt-5 flex items-center justify-center gap-2 text-xs text-[#757582] hover:text-gold-champagne transition-colors">
            <ArrowLeft size={13} /> Back to site
          </a>
        </form>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={28} />
      </div>
    );
  }

  const c = content;

  return (
    <div className="min-h-screen bg-obsidian text-foreground" data-testid="admin-studio">
      <header className="sticky top-0 z-40 bg-[rgba(10,10,12,0.9)] backdrop-blur-xl border-b border-[rgba(212,175,55,0.2)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="leading-tight">
            <p className="font-serif-lux text-lg text-[#F7F7F8]">Content Studio</p>
            <p className="font-mono-lux text-[9px] tracking-[0.3em] text-gold">EVERY EDIT PUBLISHES LIVE</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-[#B8B8C2] hover:text-gold-champagne transition-colors hidden sm:flex items-center gap-1.5">
              <ArrowLeft size={13} /> View Site
            </a>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-save-button"
              className="btn-shimmer inline-flex items-center gap-2 bg-gold text-[#0A0A0B] font-semibold text-xs tracking-wide px-5 py-2.5 hover:bg-gold-champagne disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Publishing..." : "Publish Changes"}
            </button>
            <button onClick={logout} data-testid="admin-logout-button" aria-label="Log out" className="text-[#757582] hover:text-gold-champagne transition-colors p-2">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <Group title="Navigation">
          <Field label="Brand Name" value={c.nav.brand} onChange={set(["nav", "brand"])} />
          <Field label="Brand Tagline" value={c.nav.tag} onChange={set(["nav", "tag"])} />
          <Field label="CTA Button Label" value={c.nav.cta} onChange={set(["nav", "cta"])} />
          {c.nav.links.map((l, i) => (
            <Field key={i} label={`Menu Link ${i + 1}`} value={l.label} onChange={set(["nav", "links", i, "label"])} />
          ))}
        </Group>

        <Group title="Hero">
          <Field label="Eyebrow" value={c.hero.eyebrow} onChange={set(["hero", "eyebrow"])} wide />
          <Field label="Headline Line 1 (italic gold)" value={c.hero.line1} onChange={set(["hero", "line1"])} />
          <Field label="Headline Line 2" value={c.hero.line2} onChange={set(["hero", "line2"])} />
          <Field label="Headline Line 3 — before accent" value={c.hero.line3Pre} onChange={set(["hero", "line3Pre"])} />
          <Field label="Headline Line 3 — gold accent" value={c.hero.line3Accent} onChange={set(["hero", "line3Accent"])} />
          <Field label="Headline Line 3 — after accent" value={c.hero.line3Post} onChange={set(["hero", "line3Post"])} />
          <Field label="Paragraph" value={c.hero.subtext} onChange={set(["hero", "subtext"])} textarea wide />
          <Field label="Primary Button" value={c.hero.ctaPrimary} onChange={set(["hero", "ctaPrimary"])} />
          <Field label="Secondary Button" value={c.hero.ctaSecondary} onChange={set(["hero", "ctaSecondary"])} />
          <Field label="Floating Badge Text" value={c.hero.badge} onChange={set(["hero", "badge"])} wide />
          <ImageField label="Portrait Photo" value={c.hero.portrait} onChange={set(["hero", "portrait"])} token={token} />
          {c.hero.stats.map((s, i) => (
            <SubCard key={i} title={`Stat ${i + 1}`}>
              <Field label="Number" value={s.n} onChange={set(["hero", "stats", i, "n"])} />
              <Field label="Label" value={s.l} onChange={set(["hero", "stats", i, "l"])} />
            </SubCard>
          ))}
        </Group>

        <Group title="Marquee Ribbon">
          {c.marquee.map((m, i) => (
            <Field key={i} label={`Phrase ${i + 1}`} value={m} onChange={set(["marquee", i])} />
          ))}
        </Group>

        <Group title="Manifesto">
          <Field label="Eyebrow" value={c.manifesto.eyebrow} onChange={set(["manifesto", "eyebrow"])} />
          <Field label="Heading — before accent" value={c.manifesto.headingPre} onChange={set(["manifesto", "headingPre"])} />
          <Field label="Heading — gold accent" value={c.manifesto.headingAccent} onChange={set(["manifesto", "headingAccent"])} />
          <Field label="Heading — after accent" value={c.manifesto.headingPost} onChange={set(["manifesto", "headingPost"])} />
          {c.manifesto.chapters.map((ch, i) => (
            <SubCard key={i} title={`Chapter ${ch.num} — ${ch.title}`}>
              <Field label="Chapter Number" value={ch.num} onChange={set(["manifesto", "chapters", i, "num"])} />
              <Field label="Title" value={ch.title} onChange={set(["manifesto", "chapters", i, "title"])} />
              <Field label="Pull Quote" value={ch.quote} onChange={set(["manifesto", "chapters", i, "quote"])} textarea wide />
              <Field label="Body" value={ch.body} onChange={set(["manifesto", "chapters", i, "body"])} textarea wide />
              {ch.hats
                ? ch.hats.map((h, j) => (
                    <SubCard key={j} title={h.label}>
                      <Field label="Hat Label" value={h.label} onChange={set(["manifesto", "chapters", i, "hats", j, "label"])} />
                      <Field label="Hat Name" value={h.name} onChange={set(["manifesto", "chapters", i, "hats", j, "name"])} />
                      <Field label="Hat Detail" value={h.detail} onChange={set(["manifesto", "chapters", i, "hats", j, "detail"])} textarea wide />
                    </SubCard>
                  ))
                : null}
              {ch.closing !== undefined ? (
                <Field label="Closing Line" value={ch.closing} onChange={set(["manifesto", "chapters", i, "closing"])} wide />
              ) : null}
            </SubCard>
          ))}
        </Group>

        <Group title="The Ecentra Run">
          <Field label="Eyebrow" value={c.ecentra.eyebrow} onChange={set(["ecentra", "eyebrow"])} />
          <Field label="Headline Line 1" value={c.ecentra.h1a} onChange={set(["ecentra", "h1a"])} />
          <Field label="Headline Line 2 (italic gold)" value={c.ecentra.h1b} onChange={set(["ecentra", "h1b"])} />
          <Field label="Headline Line 3" value={c.ecentra.h1c} onChange={set(["ecentra", "h1c"])} />
          <Field label="Paragraph" value={c.ecentra.paragraph} onChange={set(["ecentra", "paragraph"])} textarea wide />
          <Field label="Button Label" value={c.ecentra.cta} onChange={set(["ecentra", "cta"])} />
          <Field label="Button Link URL" value={c.ecentra.ctaUrl} onChange={set(["ecentra", "ctaUrl"])} />
          <ImageField label="Section Photo or Clip (optional)" value={c.ecentra.image} onChange={set(["ecentra", "image"])} token={token} />
          {c.ecentra.highlights.map((h, i) => (
            <SubCard key={i} title={`Highlight ${i + 1}`}>
              <Field label="Key" value={h.k} onChange={set(["ecentra", "highlights", i, "k"])} />
              <Field label="Value" value={h.v} onChange={set(["ecentra", "highlights", i, "v"])} />
            </SubCard>
          ))}
        </Group>

        <Group title="Four Pillars">
          <Field label="Eyebrow" value={c.pillars.eyebrow} onChange={set(["pillars", "eyebrow"])} />
          <Field label="Heading Line 1" value={c.pillars.headingPre} onChange={set(["pillars", "headingPre"])} />
          <Field label="Heading Line 2 (italic gold)" value={c.pillars.headingAccent} onChange={set(["pillars", "headingAccent"])} />
          <Field label="Side Note" value={c.pillars.sub} onChange={set(["pillars", "sub"])} textarea />
          {c.pillars.items.map((p, i) => (
            <SubCard key={i} title={`Pillar ${i + 1} — ${p.name}`}>
              <Field label="Name" value={p.name} onChange={set(["pillars", "items", i, "name"])} />
              <Field label="Title" value={p.title} onChange={set(["pillars", "items", i, "title"])} />
              <Field label="Description" value={p.desc} onChange={set(["pillars", "items", i, "desc"])} textarea wide />
            </SubCard>
          ))}
        </Group>

        <Group title="Wellness & Transformation Sections">
          <Field label="Eyebrow" value={c.twin.eyebrow} onChange={set(["twin", "eyebrow"])} wide />
          {c.twin.sections.map((s, i) => (
            <SubCard key={i} title={`Section ${i + 1} — ${s.title}`}>
              <Field label="Tag" value={s.tag} onChange={set(["twin", "sections", i, "tag"])} />
              <Field label="Title" value={s.title} onChange={set(["twin", "sections", i, "title"])} />
              <Field label="Subtitle" value={s.subtitle} onChange={set(["twin", "sections", i, "subtitle"])} />
              <Field label="Description" value={s.desc} onChange={set(["twin", "sections", i, "desc"])} textarea wide />
              <Field label="Link Label" value={s.linkLabel} onChange={set(["twin", "sections", i, "linkLabel"])} />
              <Field label="Link URL" value={s.linkUrl} onChange={set(["twin", "sections", i, "linkUrl"])} />
              <ImageField label="Background Image" value={s.img} onChange={set(["twin", "sections", i, "img"])} token={token} />
            </SubCard>
          ))}
        </Group>

        <Group title="Contact Section">
          <Field label="Eyebrow" value={c.contact.eyebrow} onChange={set(["contact", "eyebrow"])} />
          <Field label="Heading — before accent" value={c.contact.headingPre} onChange={set(["contact", "headingPre"])} />
          <Field label="Heading — gold accent" value={c.contact.headingAccent} onChange={set(["contact", "headingAccent"])} />
          <Field label="Heading — after accent" value={c.contact.headingPost} onChange={set(["contact", "headingPost"])} />
          <Field label="Paragraph" value={c.contact.subtext} onChange={set(["contact", "subtext"])} textarea wide />
          <Field label="Quote" value={c.contact.quote} onChange={set(["contact", "quote"])} textarea wide />
          <Field label="Quote Attribution" value={c.contact.quoteBy} onChange={set(["contact", "quoteBy"])} />
          <Field label="Submit Button Label" value={c.contact.submit} onChange={set(["contact", "submit"])} />
          {c.contact.interests.map((o, i) => (
            <Field key={i} label={`Form Option ${i + 1}`} value={o} onChange={set(["contact", "interests", i])} />
          ))}
        </Group>

        <Group title="Footer">
          <Field label="Quote — before accent" value={c.footer.quotePre} onChange={set(["footer", "quotePre"])} textarea wide />
          <Field label="Quote — gold accent" value={c.footer.quoteAccent} onChange={set(["footer", "quoteAccent"])} />
          <Field label="Quote — after accent" value={c.footer.quotePost} onChange={set(["footer", "quotePost"])} />
          <Field label="Quote Attribution" value={c.footer.quoteBy} onChange={set(["footer", "quoteBy"])} />
          <Field label="Facebook Link Label" value={c.footer.fbLabel} onChange={set(["footer", "fbLabel"])} />
          <Field label="Facebook URL" value={c.footer.fbUrl} onChange={set(["footer", "fbUrl"])} />
          <Field label="Copyright Line" value={c.footer.copyright} onChange={set(["footer", "copyright"])} wide />
        </Group>
      </main>
    </div>
  );
}
